/* ==================================================================
   META PIXEL — suivi publicitaire (chargé UNIQUEMENT après consentement)
   ------------------------------------------------------------------
   - Événements envoyés : PageView (par navigation SPA), ViewContent,
     AddToCart, InitiateCheckout. JAMAIS Purchase (la caisse Shopify
     l'envoie déjà — sinon achats comptés en double).
   - Le pixel ne se charge qu'après « Aceptar » dans la bannière cookies.
     Si « Rechazar » : aucun script Meta, aucun événement.
   - Attribution : fbclid + utm_* capturés à l'arrivée, stockés 30 jours
     en localStorage, transmis à l'URL de la caisse Shopify.
================================================================== */

/* L'ID du pixel est public (il part dans le bundle) : constante de secours
   au cas où la variable d'environnement manquerait au build. */
const PIXEL_ID = (process.env.REACT_APP_META_PIXEL_ID || "2129228557956348").trim();
const CONSENT_KEY = "marketing_consent";
const CONSENT_MAX_AGE = 180 * 24 * 60 * 60; // 6 mois (recommandation CNIL/AEPD)
const ATTR_KEY = "ad_attribution_v1";
const ATTR_TTL = 30 * 24 * 60 * 60 * 1000;

/* Le choix est stocké dans un COOKIE (et non plus dans localStorage) :
   quand un visiteur « supprime ses cookies », son choix est effacé et le
   bandeau réapparaît — comportement attendu de tout bandeau RGPD.
   Avant, un seul clic « Rechazar » restait gravé dans localStorage :
   supprimer les cookies ne le vidait pas → plus de bandeau, plus de pixel,
   Pixel Helper « aucun pixel » pour toujours. */
function readConsentCookie() {
    if (typeof document === "undefined") return null;
    const m = document.cookie.match(/(?:^|;\s*)marketing_consent=(accepted|rejected)(?:;|$)/);
    return m ? m[1] : null;
}

function writeConsentCookie(value, maxAge = CONSENT_MAX_AGE) {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${CONSENT_KEY}=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

/* Migration de l'ancien stockage localStorage : un « accepted » est
   conservé (recopié en cookie), un « rejected » est oublié pour que le
   bandeau soit reproposé une fois. */
function migrateLegacyConsent() {
    try {
        const legacy = localStorage.getItem(CONSENT_KEY);
        if (legacy === null) return;
        localStorage.removeItem(CONSENT_KEY);
        if (legacy === "accepted" && !readConsentCookie()) writeConsentCookie("accepted");
    } catch {
        /* localStorage indisponible : rien à migrer */
    }
}

/* "accepted" | "rejected" | null (pas encore choisi) */
export function getMarketingConsent() {
    migrateLegacyConsent();
    return readConsentCookie();
}

export const hasMarketingConsent = () => getMarketingConsent() === "accepted";

export function setMarketingConsent(value) {
    writeConsentCookie(value);
    window.dispatchEvent(new CustomEvent("marketing-consent", { detail: value }));
}

/* Lien « Cookies » du footer : efface le choix pour rouvrir le bandeau. */
export function clearMarketingConsent() {
    writeConsentCookie("", 0);
    try {
        localStorage.removeItem(CONSENT_KEY);
    } catch {
        /* ignore */
    }
}

function loadScriptOnce() {
    if (document.getElementById("meta-pixel-script")) return;
    /* Snippet officiel Meta (file d'attente avant chargement). */
    if (!window.fbq) {
        const n = (window.fbq = function (...args) {
            n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
        });
        /* window._fbq est attendu par fbevents.js : sans lui, la lib signale
           « Multiple pixels with conflicting versions » au Pixel Helper. */
        if (!window._fbq) window._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        n._asa = true;
    }
    const t = document.createElement("script");
    t.async = true;
    t.id = "meta-pixel-script";
    t.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(t);
}

export function initPixel() {
    if (!PIXEL_ID || !hasMarketingConsent()) return false;
    /* Crée d'abord le stub window.fbq + injecte fbevents.js, PUIS init. */
    loadScriptOnce();
    if (!window.__metaPixelInitialized) {
        window.fbq("init", PIXEL_ID);
        window.__metaPixelInitialized = true;
    }
    return true;
}

export function track(name, params = {}) {
    if (window.fbq && window.__metaPixelInitialized && hasMarketingConsent())
        window.fbq("track", name, params);
}

/* --- Attribution publicitaire (fbclid / utm_*) ------------------- */

const ATTR_KEYS = ["fbclid", "utm_source", "utm_medium", "utm_campaign", "utm_content"];

export function captureAttribution() {
    const p = new URLSearchParams(window.location.search);
    const incoming = Object.fromEntries(
        ATTR_KEYS.filter((k) => p.get(k)).map((k) => [k, p.get(k)])
    );
    if (Object.keys(incoming).length)
        localStorage.setItem(
            ATTR_KEY,
            JSON.stringify({ ...incoming, expiresAt: Date.now() + ATTR_TTL })
        );
}

export function getAttribution() {
    try {
        const x = JSON.parse(localStorage.getItem(ATTR_KEY) || "null");
        if (!x || x.expiresAt < Date.now()) {
            localStorage.removeItem(ATTR_KEY);
            return {};
        }
        const { expiresAt, ...values } = x;
        return values;
    } catch {
        return {};
    }
}

/* Cookies Meta first-party posés par fbevents.js sur notre domaine
   (seulement si le client a consenti — le pixel est alors chargé). */
const readCookie = (name) => {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : undefined;
};
const readMetaCookies = () => ({ _fbp: readCookie("_fbp"), _fbc: readCookie("_fbc") });

/* Ajoute fbclid, utm_*, _fbp et _fbc à l'URL de la caisse (sans écraser). */
export function appendAttribution(checkoutUrl) {
    try {
        const u = new URL(checkoutUrl);
        const src = { ...getAttribution(), ...readMetaCookies() };
        Object.entries(src).forEach(([k, v]) => {
            if (v && !u.searchParams.has(k)) u.searchParams.set(k, v);
        });
        return u.toString();
    } catch {
        return checkoutUrl;
    }
}
